<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :header="$t('adminClassificationPage.viewSelector.title')"
    :style="{ width: '550px', maxWidth: '95vw' }"
  >
    <!-- Search bar -->
    <div v-if="!readonly" class="view-selector-search">
      <PrimeIconField>
        <PrimeInputIcon class="ri-search-line" />
        <PrimeInputText
          v-model="searchQuery"
          :placeholder="$t('adminClassificationPage.viewSelector.search')"
          size="small"
          fluid
        />
        <PrimeInputIcon
          v-if="searchQuery"
          class="ri-close-line view-selector-clear"
          @click="searchQuery = ''"
        />
      </PrimeIconField>
    </div>

    <div v-if="loading" class="text-center p-3">
      <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
    </div>

    <template v-else>
      <!-- Search results -->
      <div v-if="isSearching" class="view-selector-section">
        <div class="view-selector-section-header">
          {{ $t("adminClassificationPage.viewSelector.searchResults") }}
        </div>
        <div class="view-selector-list">
          <div
            v-for="view in searchResults"
            :key="'search-' + view"
            class="view-selector-item"
          >
            <PrimeCheckbox
              :modelValue="isSelected(view)"
              :binary="true"
              @update:modelValue="toggleView(view, $event)"
            />
            <span class="view-selector-label">{{ view }}</span>
          </div>
          <div
            v-if="searchResults.length === 0"
            class="text-sm text-color-secondary p-2"
          >
            {{ $t("adminClassificationPage.viewSelector.noResults") }}
          </div>
        </div>
      </div>

      <template v-else>
        <!-- Selected views (draggable) -->
        <div v-if="localSelected.length > 0" class="view-selector-section">
          <div class="view-selector-section-header">
            {{ $t("adminClassificationPage.viewSelector.selected") }}
          </div>
          <div class="view-selector-list">
            <div
              v-for="(view, index) in localSelected"
              :key="view"
              class="view-selector-item"
              :class="{ 'drag-over': !readonly && dragOverIndex === index }"
              :draggable="!readonly"
              @dragstart="!readonly && onDragStart(index, $event)"
              @dragover.prevent="!readonly && onDragOver(index)"
              @dragleave="!readonly && onDragLeave()"
              @drop="!readonly && onDrop(index)"
              @dragend="!readonly && onDragEnd()"
            >
              <i v-if="!readonly" class="ri-draggable drag-handle" />
              <PrimeCheckbox
                :modelValue="true"
                :binary="true"
                :disabled="readonly"
                @update:modelValue="toggleView(view, $event)"
              />
              <span class="view-selector-label">{{ view }}</span>
              <div v-if="!readonly" class="view-selector-arrows">
                <PrimeButton
                  type="button"
                  icon="ri-arrow-up-s-line"
                  size="small"
                  severity="secondary"
                  text
                  rounded
                  :disabled="index === 0"
                  @click="moveView(index, -1)"
                />
                <PrimeButton
                  type="button"
                  icon="ri-arrow-down-s-line"
                  size="small"
                  severity="secondary"
                  text
                  rounded
                  :disabled="index === localSelected.length - 1"
                  @click="moveView(index, 1)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Available views (not selected) -->
        <div
          v-if="!readonly && unselectedViews.length > 0"
          class="view-selector-section"
        >
          <div class="view-selector-section-header">
            {{ $t("adminClassificationPage.viewSelector.available") }}
          </div>
          <div class="view-selector-list">
            <div
              v-for="view in unselectedViews"
              :key="view"
              class="view-selector-item"
            >
              <PrimeCheckbox
                :modelValue="false"
                :binary="true"
                @update:modelValue="toggleView(view, $event)"
              />
              <span class="view-selector-label">{{ view }}</span>
            </div>
          </div>
        </div>
      </template>
    </template>

    <template #footer>
      <PrimeButton
        :label="$t('cancel')"
        severity="secondary"
        text
        @click="$emit('update:visible', false)"
      />
      <PrimeButton
        v-if="!readonly"
        :label="$t('adminClassificationPage.viewSelector.validate')"
        icon="ri-check-line"
        @click="validate"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";

export default {
  name: "ViewSelectorPopup",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeCheckbox: Checkbox,
    PrimeInputText: InputText,
    PrimeIconField: IconField,
    PrimeInputIcon: InputIcon,
  },
  props: {
    visible: Boolean,
    selectedViews: {
      type: Array,
      default: () => [],
    },
    availableViews: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:visible", "update:selectedViews"],
  data() {
    return {
      localSelected: [],
      searchQuery: "",
      dragIndex: null,
      dragOverIndex: null,
    };
  },
  computed: {
    allFilenames() {
      return this.availableViews.map((v) => v.value || v);
    },
    unselectedViews() {
      return this.allFilenames.filter((f) => !this.localSelected.includes(f));
    },
    isSearching() {
      return this.searchQuery.trim().length > 0;
    },
    searchResults() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return [];
      }
      return this.allFilenames.filter((f) => f.toLowerCase().includes(query));
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.localSelected = [...(this.selectedViews || [])];
        this.searchQuery = "";
      }
    },
  },
  methods: {
    isSelected(view) {
      return this.localSelected.includes(view);
    },
    toggleView(view, checked) {
      if (checked) {
        if (!this.localSelected.includes(view)) {
          this.localSelected.push(view);
        }
      } else {
        this.localSelected = this.localSelected.filter((v) => v !== view);
      }
    },
    moveView(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.localSelected.length) {
        return;
      }
      const arr = [...this.localSelected];
      const item = arr.splice(index, 1)[0];
      arr.splice(target, 0, item);
      this.localSelected = arr;
    },
    onDragStart(index, event) {
      this.dragIndex = index;
      event.dataTransfer.effectAllowed = "move";
    },
    onDragOver(index) {
      this.dragOverIndex = index;
    },
    onDragLeave() {
      this.dragOverIndex = null;
    },
    onDrop(targetIndex) {
      this.dragOverIndex = null;
      if (this.dragIndex === null || this.dragIndex === targetIndex) {
        return;
      }
      const arr = [...this.localSelected];
      const item = arr.splice(this.dragIndex, 1)[0];
      arr.splice(targetIndex, 0, item);
      this.localSelected = arr;
    },
    onDragEnd() {
      this.dragIndex = null;
      this.dragOverIndex = null;
    },
    validate() {
      this.$emit("update:selectedViews", [...this.localSelected]);
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped lang="scss">
.view-selector-search {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--p-surface-border);
  margin-bottom: 0.5rem;

  :deep(.view-selector-clear) {
    cursor: pointer;
    pointer-events: auto;
    border-radius: 50%;

    &:hover {
      background: var(--p-surface-hover);
    }
  }
}

.view-selector-section {
  &:not(:last-child) {
    border-bottom: 1px solid var(--p-surface-border);
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
  }
}

.view-selector-section-header {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
  padding: 0.5rem 0.25rem 0.25rem;
}

.view-selector-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 300px;
  overflow-y: auto;
}

.view-selector-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  transition: background 0.15s;

  &:hover {
    background: var(--p-surface-hover);
  }

  &.drag-over {
    border-top: 2px solid var(--p-primary-color);
  }
}

.drag-handle {
  cursor: grab;
  color: var(--p-text-muted-color);
  font-size: 1rem;

  &:active {
    cursor: grabbing;
  }
}

.view-selector-label {
  flex: 1;
  font-size: 0.875rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.view-selector-arrows {
  display: flex;
  margin-left: auto;

  :deep(.p-button) {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
  }
}
</style>
